/**
 * Format timestamp for yaps, shouts, and user-generated content
 * 
 * Format rules:
 * - < 1 min: "Now"
 * - < 1 hour: "<number>m ago"
 * - < 24 hours: "<number>h ago"
 * - < 7 days: "<number>d ago"
 * - < 4 weeks: "<number>w ago"
 * - < 6 months: "<number>mo ago"
 * - >= 6 months: "Day Month, Year" (e.g., "15 January, 2024")
 */
export function formatTimeAgo(timestamp: string | Date | number): string {
    try {
        // Handle various timestamp formats
        let date: Date;

        if (timestamp instanceof Date) {
            date = timestamp;
        } else if (typeof timestamp === 'string') {
            // If it's already formatted (contains "ago"), return as is
            const lower = timestamp.toLowerCase();
            // Already a relative "now" label → normalise to "Now".
            if (lower === 'just now' || lower === 'now') {
                return 'Now';
            }
            // Already formatted (e.g. "5m ago") → return as-is.
            if (timestamp.includes('ago')) {
                return timestamp;
            }
            date = new Date(timestamp);
        } else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else {
            return 'Now';
        }

        // Validate date
        if (isNaN(date.getTime())) {
            return 'Now';
        }

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        // Calculate time differences
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        
        // DEBUG: Log weird cases where old messages show as "Just now"
        if (diffMinutes < 1 && diffMinutes > -5 && timestamp.toString().length > 15) {
             // Only log if it looks like a real timestamp but calc is close to 0
             // console.log(`[TimeFormat] Diff: ${diffMinutes}m (${diffMs}ms) | Now: ${now.toISOString()} | Msg: ${date.toISOString()} | Input: ${timestamp}`);
        } else if (isNaN(diffMinutes)) {
             console.error(`[TimeFormat] NaN diff for input:`, timestamp);
        }
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30); // Approximate

        // Format based on time difference
        // Handle future dates (clock skew) gracefully
        if (diffMinutes < 1) {
            return 'Now';
        } else if (diffMinutes < 60) {
            return `${diffMinutes}m ago`;
        } else if (diffHours < 24) {
            return `${diffHours}h ago`;
        } else if (diffDays < 7) {
            return `${diffDays}d ago`;
        } else if (diffDays < 30) {
            return `${diffWeeks}w ago`;
        } else if (diffMonths < 6) {
            return `${diffMonths}mo ago`;
        } else {
            // Format as "Day Month, Year" (international format)
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];

            const day = date.getDate();
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();

            return `${day} ${month}, ${year}`;
        }
    } catch (error) {
        console.error('Error formatting time:', error);
        return 'Just now';
    }
}

/**
 * Format timestamp for Firestore timestamps
 * Handles Firestore Timestamp objects that have a toDate() method
 */
export function formatFirestoreTime(timestamp: any): string {
    if (!timestamp) return 'Now';

    // Handle Firestore Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return formatTimeAgo(timestamp.toDate());
    }

    return formatTimeAgo(timestamp);
}

export function calculateAge(dob?: any): number | null {
    if (!dob) return null;
    
    try {
        let birthDate: Date;
        
        if (typeof dob === 'string') {
            const trimmed = dob.trim();
            if (!trimmed) return null;
            
            // Handle YYYY-MM-DD (ISO)
            const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
            if (isoMatch) {
                const year = parseInt(isoMatch[1], 10);
                const month = parseInt(isoMatch[2], 10) - 1;
                const day = parseInt(isoMatch[3], 10);
                birthDate = new Date(year, month, day);
            } else {
                // Secondary fallback for MM/DD/YYYY or DD/MM/YYYY
                const parts = trimmed.split(/[-/]/).map(p => parseInt(p, 10));
                if (parts.length === 3) {
                    // Try to guess which part is the year (usually > 1900)
                    if (parts[2] > 1900) {
                        // MM/DD/YYYY or DD/MM/YYYY - default to MM/DD
                        birthDate = new Date(parts[2], parts[0] - 1, parts[1]);
                    } else if (parts[0] > 1900) {
                        // YYYY/MM/DD
                        birthDate = new Date(parts[0], parts[1] - 1, parts[2]);
                    } else {
                        birthDate = new Date(trimmed);
                    }
                } else {
                    birthDate = new Date(trimmed);
                }
            }
        } else if (dob instanceof Date) {
            birthDate = dob;
        } else if (dob && typeof dob.toDate === 'function') {
            // Handle Firestore Timestamp
            birthDate = dob.toDate();
        } else {
            return null;
        }

        if (isNaN(birthDate.getTime())) return null;

        const today = new Date();
        const tYear = today.getFullYear();
        const tMonth = today.getMonth();
        const tDay = today.getDate();

        const bYear = birthDate.getFullYear();
        const bMonth = birthDate.getMonth();
        const bDay = birthDate.getDate();

        let age = tYear - bYear;
        
        // Accurate month/day boundary check in local time
        if (tMonth < bMonth || (tMonth === bMonth && tDay < bDay)) {
            age--;
        }
        
        return age;
    } catch (error) {
        console.error('Error calculating age:', error);
        return null;
    }
}
